// The margin screen's arithmetic: the report's figures, moved by a what-if and
// by whether one-time fees count.
//
// The report states the month as it was. A what-if can change three things:
//
//  * PRICE. A flat line's monthly amount, or a rated line's price per unit, can
//    be typed; the percentage slider scales every price nobody typed. The
//    partner's share of each line stays at its contracted split.
//  * MINUTES. The month's volume, scaled from what was measured. A rated line
//    bills more units, a prepaid block covers what it has left before overage
//    starts, and every cost line not marked fixed grows with it.
//  * FIXED COSTS. A cost line marked fixed stays put when the minutes move.

import type { MarginCustomer, MarginRevenueLine } from "@crm/shared";

export interface PriceEdits {
  adjPct: number;
  /** A flat line's billed gross, in minor units of the report currency, keyed by line. */
  lineGross: Readonly<Record<string, number>>;
  /** A rated line's price per unit, in minor units of the report currency, keyed by line. */
  lineRate: Readonly<Record<string, number>>;
  /** The month's minutes. Null keeps what was measured. */
  minutes: number | null;
  /** Cost lines that do not grow with minutes, keyed by line. */
  fixedCosts: Readonly<Record<string, true>>;
}

export const NO_EDITS: PriceEdits = {
  adjPct: 0,
  lineGross: {},
  lineRate: {},
  minutes: null,
  fixedCosts: {},
};

export function hasEdits(edits: PriceEdits): boolean {
  return (
    edits.adjPct !== 0 ||
    Object.keys(edits.lineGross).length > 0 ||
    Object.keys(edits.lineRate).length > 0 ||
    edits.minutes !== null ||
    Object.keys(edits.fixedCosts).length > 0
  );
}

/** A price edit a line can take: a flat amount, or a price per unit. */
export type LinePriceKind = "flat" | "rate";

export interface RevenueLineFigures {
  key: string;
  label: string;
  recurring: boolean;
  fromPool: boolean;
  priceKind: LinePriceKind;
  unit: string;
  baseGrossMinor: number;
  grossMinor: number;
  oursMinor: number;
  /** Our share of the line, 0-100. */
  sharePct: number;
  /** Price per unit now, for a rated line. */
  rateMinor: number | null;
  baseRateMinor: number | null;
  /** Units billed now, for a rated line. */
  quantity: number | null;
  /** A price was typed for this line. */
  priced: boolean;
  source: MarginRevenueLine;
}

export interface CostLineFigures {
  key: string;
  baseCostMinor: number;
  costMinor: number;
  ourCostMinor: number;
  fixed: boolean;
}

export interface MarginFigures {
  lines: RevenueLineFigures[];
  costs: CostLineFigures[];
  /** Minutes now, and as measured. Null when no tenant is bound. */
  minutes: number | null;
  baseMinutes: number | null;
  conversations: number | null;
  billedMinor: number;
  partnerShareMinor: number;
  oursMinor: number;
  /** Null when the report has no cost for the customer. */
  costMinor: number | null;
  ourCostMinor: number | null;
  partnerCostMinor: number | null;
  contributionMinor: number | null;
  marginPct: number | null;
  /** The change to every recurring price, in percent, at which contribution is zero. */
  breakEvenPct: number | null;
  /** The recurring gross, at the month's split, at which contribution is zero. */
  breakEvenGrossMinor: number | null;
}

function parseMinor(value: string | null): number | null {
  if (value === null) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function deriveMargin(
  customer: MarginCustomer,
  edits: PriceEdits,
  includeOneTime: boolean,
): MarginFigures {
  const factor = 1 + edits.adjPct / 100;
  const baseMinutes = customer.volume?.minutes ?? null;
  const minutes = baseMinutes === null ? null : (edits.minutes ?? baseMinutes);
  // How much the month's volume moved. Nothing measured means nothing to scale.
  const volume =
    baseMinutes === null || baseMinutes === 0 || minutes === null ? 1 : minutes / baseMinutes;
  const fallbackShare = customer.revenue_share_pct ?? 100;

  const lines = customer.revenue_lines
    .filter((line) => line.recurring || includeOneTime)
    .map((line) => deriveLine(line, edits, factor, volume, fallbackShare));

  const costs = customer.cost_lines.map((line): CostLineFigures => {
    const fixed = edits.fixedCosts[line.key] === true;
    const scale = fixed ? 1 : volume;
    return {
      key: line.key,
      baseCostMinor: line.cost_minor,
      costMinor: Math.round(line.cost_minor * scale),
      ourCostMinor: Math.round(line.our_cost_minor * scale),
      fixed,
    };
  });

  const billed = lines.reduce((sum, line) => sum + line.grossMinor, 0);
  const ours = lines.reduce((sum, line) => sum + line.oursMinor, 0);
  const oneTimeOurs = includeOneTime ? customer.one_time_our_share_minor : 0;
  const known = customer.cost_basis !== "none";
  const cost = known ? costs.reduce((sum, line) => sum + line.costMinor, 0) : null;
  const ourCost = known ? costs.reduce((sum, line) => sum + line.ourCostMinor, 0) : null;
  const contribution = ourCost === null ? null : ours - ourCost;
  const recurringOurs = customer.recurring_our_share_minor;
  const keptPerBilled =
    customer.recurring_gross_minor === 0 ? null : recurringOurs / customer.recurring_gross_minor;

  return {
    lines,
    costs,
    minutes,
    baseMinutes,
    conversations: customer.volume === null ? null : customer.volume.conversations * volume,
    billedMinor: billed,
    partnerShareMinor: billed - ours,
    oursMinor: ours,
    costMinor: cost,
    ourCostMinor: ourCost,
    partnerCostMinor: cost === null || ourCost === null ? null : cost - ourCost,
    contributionMinor: contribution,
    marginPct: contribution === null || ours === 0 ? null : (contribution * 100) / ours,
    breakEvenPct:
      ourCost === null || recurringOurs === 0
        ? null
        : ((ourCost - oneTimeOurs) / recurringOurs - 1) * 100,
    breakEvenGrossMinor:
      ourCost === null || keptPerBilled === null || keptPerBilled === 0
        ? null
        : Math.max(0, Math.round((ourCost - oneTimeOurs) / keptPerBilled)),
  };
}

function deriveLine(
  line: MarginRevenueLine,
  edits: PriceEdits,
  factor: number,
  volume: number,
  fallbackShare: number,
): RevenueLineFigures {
  const sharePct =
    line.gross_minor === 0 ? fallbackShare : (line.our_share_minor * 100) / line.gross_minor;
  const baseRate = parseMinor(line.unit_price_minor);
  const common = {
    key: line.key,
    label: line.label,
    recurring: line.recurring,
    fromPool: line.from_pool,
    unit: line.unit,
    baseGrossMinor: line.gross_minor,
    sharePct,
    source: line,
  };

  // One-time fees are what they were; no what-if touches them.
  if (!line.recurring) {
    return {
      ...common,
      priceKind: baseRate === null ? "flat" : "rate",
      grossMinor: line.gross_minor,
      oursMinor: line.our_share_minor,
      rateMinor: baseRate,
      baseRateMinor: baseRate,
      quantity: line.quantity,
      priced: false,
    };
  }

  if (baseRate === null) {
    const typed = edits.lineGross[line.key];
    const gross = typed ?? Math.round(line.gross_minor * factor);
    return {
      ...common,
      priceKind: "flat",
      grossMinor: gross,
      oursMinor:
        gross === line.gross_minor ? line.our_share_minor : Math.round((gross * sharePct) / 100),
      rateMinor: null,
      baseRateMinor: null,
      quantity: null,
      priced: typed !== undefined,
    };
  }

  const typedRate = edits.lineRate[line.key];
  const rate = typedRate ?? baseRate * factor;
  // Units billed at this volume: a covered line bills only what the block cannot cover.
  const draw = line.prepaid_draw;
  const baseQuantity = line.quantity ?? 0;
  const quantity =
    draw === null
      ? baseQuantity * volume
      : Math.max(0, draw.consumed_quantity * volume - draw.available_quantity);
  const unchanged = rate === baseRate && volume === 1;
  const gross = unchanged ? line.gross_minor : Math.round(quantity * rate);
  return {
    ...common,
    priceKind: "rate",
    grossMinor: gross,
    oursMinor: unchanged ? line.our_share_minor : Math.round((gross * sharePct) / 100),
    rateMinor: rate,
    baseRateMinor: baseRate,
    quantity: unchanged ? baseQuantity : quantity,
    priced: typedRate !== undefined,
  };
}

/** Per conversation and per minute, from the figures now. Null where volume is unknown or zero. */
export interface UnitEconomics {
  costPerConversationMinor: number | null;
  costPerMinuteMinor: number | null;
  oursPerMinuteMinor: number | null;
  contributionPerMinuteMinor: number | null;
}

export function unitEconomics(figures: MarginFigures): UnitEconomics {
  const per = (value: number | null, by: number | null): number | null =>
    value === null || by === null || by === 0 ? null : value / by;
  return {
    costPerConversationMinor: per(figures.costMinor, figures.conversations),
    costPerMinuteMinor: per(figures.costMinor, figures.minutes),
    oursPerMinuteMinor: per(figures.oursMinor, figures.minutes),
    contributionPerMinuteMinor: per(figures.contributionMinor, figures.minutes),
  };
}

export interface MarginSums {
  billedMinor: number;
  oursMinor: number;
  costMinor: number;
  ourCostMinor: number;
  partnerCostMinor: number;
  contributionMinor: number;
  /** Over the customers whose cost is known. */
  marginPct: number | null;
  withoutCost: number;
}

export function sumMargins(figures: readonly MarginFigures[]): MarginSums {
  let costedOurs = 0;
  const sums: MarginSums = {
    billedMinor: 0,
    oursMinor: 0,
    costMinor: 0,
    ourCostMinor: 0,
    partnerCostMinor: 0,
    contributionMinor: 0,
    marginPct: null,
    withoutCost: 0,
  };
  for (const f of figures) {
    sums.billedMinor += f.billedMinor;
    sums.oursMinor += f.oursMinor;
    if (f.contributionMinor === null) {
      sums.withoutCost += 1;
      continue;
    }
    costedOurs += f.oursMinor;
    sums.costMinor += f.costMinor ?? 0;
    sums.ourCostMinor += f.ourCostMinor ?? 0;
    sums.partnerCostMinor += f.partnerCostMinor ?? 0;
    sums.contributionMinor += f.contributionMinor;
  }
  sums.marginPct = costedOurs === 0 ? null : (sums.contributionMinor * 100) / costedOurs;
  return sums;
}

export type MarginHealth = "healthy" | "thin" | "breakEven" | "losing" | "unknown";

export function marginHealth(figures: MarginFigures): MarginHealth {
  const { marginPct, contributionMinor } = figures;
  if (marginPct === null || contributionMinor === null) return "unknown";
  if (marginPct >= 50) return "healthy";
  if (marginPct >= 15) return "thin";
  return contributionMinor < 0 ? "losing" : "breakEven";
}

/** "3,000 min prepaid · $0.80/min · 327 used" — the words for a line's prepaid terms. */
export function prepaidDescription(
  line: MarginRevenueLine,
  unitPrice: (minor: number) => string,
): string | null {
  const unit = unitShortName(line.prepaid_block?.unit ?? line.prepaid_draw?.unit ?? line.unit);
  const block = line.prepaid_block;
  if (block !== null) {
    const price = parseMinor(block.unit_price_minor);
    return [
      `${formatUnits(block.quantity)} ${unit} prepaid`,
      price === null ? null : `${unitPrice(price)}/${unit}`,
      `${formatUnits(block.drawn_quantity)} used`,
    ]
      .filter((part) => part !== null)
      .join(" · ");
  }
  const draw = line.prepaid_draw;
  if (draw !== null) {
    const over = Math.max(0, draw.consumed_quantity - draw.available_quantity);
    return `${formatUnits(draw.consumed_quantity)} ${unit} used · ${
      over > 0 ? `${formatUnits(over)} over the prepaid` : "all covered by prepaid"
    }`;
  }
  return null;
}

const UNIT_SHORT: Readonly<Record<string, string>> = {
  minute: "min",
  carrier_minute: "min",
  call: "call",
  message: "msg",
  usage: "use",
  period: "mo",
};

export function unitShortName(unit: string): string {
  return UNIT_SHORT[unit] ?? unit.replace(/_/g, " ");
}

export function formatUnits(quantity: number): string {
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: quantity < 10 ? 1 : 0 }).format(
    quantity,
  );
}
